// frontend/src/components/Layout.js
import React, { useMemo, useState } from 'react';
import { Outlet, Link as RouterLink, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Paper,
  Tooltip,
  Avatar,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountBalance as ReconciliationIcon,
  Logout as LogoutIcon,
  Timeline as TimelineIcon,
  Layers as LayersIcon,
  ShowChart as SalesIcon,
  ReportProblem as ExceptionsIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import StarIcon from '@mui/icons-material/Star';
import PaymentsIcon from '@mui/icons-material/Payments';
import StorefrontIcon from '@mui/icons-material/Storefront';
import GavelIcon from '@mui/icons-material/Gavel';
import SummarizeIcon from '@mui/icons-material/Summarize';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InsightsIcon from '@mui/icons-material/Insights';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import TuneIcon from '@mui/icons-material/Tune';

import { useAuth } from '../contexts/AuthContext';
import { usePlatform } from '../contexts/PlatformContext';

const drawerWidth = 270;

const safeRole = (u) => String(u?.role || '').toLowerCase();

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Which nav groups the user has opened or closed by hand. A group not listed
  // here follows the current route, so the page you are on is always visible.
  const [openNavGroups, setOpenNavGroups] = useState({});

  const { user, logout } = useAuth();
  const { platform, clearPlatform } = usePlatform();

  const params = useParams();
  const location = useLocation();

  const handleDrawerToggle = () => setMobileOpen((m) => !m);

  const role = safeRole(user);
  const isAdminPlatform = platform?.type === 'ADMIN' || role === 'admin';
  const isDirectorsPlatform = platform?.type === 'DIRECTORS' || role === 'director';
  const isFinancePlatform =
    !isAdminPlatform &&
    !isDirectorsPlatform &&
    (platform?.type === 'FINANCE' || role === 'finance' || role === 'accountant');

  /* Read-only. The menu hides what they cannot use; the server refuses it. */
  const isAccountant = role === 'accountant';
  const isSitePlatform =
    platform?.type === 'SITE' && !isAdminPlatform && !isDirectorsPlatform && !isFinancePlatform;

  const effectiveSiteCode = useMemo(() => {
    return params.siteCode || platform?.siteCode || localStorage.getItem('selectedSiteCode') || '';
  }, [params.siteCode, platform?.siteCode]);

  const effectiveSiteName = useMemo(() => {
    const fromPlatform = String(platform?.siteName || '').trim();
    if (fromPlatform) return fromPlatform;

    const fromStorage = String(localStorage.getItem('selectedSiteName') || '').trim();
    if (fromStorage) return fromStorage;

    const fromUser = user?.site && typeof user.site === 'object' ? String(user.site.name || '').trim() : '';
    if (fromUser) return fromUser;

    return '';
  }, [platform?.siteName, user?.site]);

  const siteLabel = useMemo(() => {
    if (isAdminPlatform) return 'System Admin';
    if (isDirectorsPlatform) return 'All Sites — Directors';
    if (isFinancePlatform) return 'All Sites — Finance';

    if (isSitePlatform) {
      const code = String(effectiveSiteCode || '').trim();
      const name = String(effectiveSiteName || '').trim();

      if (name && code) return `${name} (${code})`;
      if (name) return name;
      if (code) return code;

      return 'Site';
    }

    return 'All Sites';
  }, [
    isAdminPlatform,
    isDirectorsPlatform,
    isFinancePlatform,
    isSitePlatform,
    effectiveSiteName,
    effectiveSiteCode,
  ]);

  const handleLogout = () => {
    clearPlatform?.();
    logout?.();
  };

  const isActive = (to) => location.pathname.startsWith(to);

  const displayName = useMemo(() => {
    const fn = String(user?.firstName || '').trim();
    const ln = String(user?.lastName || '').trim();
    const full = `${fn} ${ln}`.trim();
    return full || user?.email || 'User';
  }, [user]);

  const emailLabel = useMemo(() => String(user?.email || '').trim(), [user]);
  const roleChipLabel = useMemo(() => String(user?.role || '').toUpperCase(), [user?.role]);
  const pageMeta = useMemo(() => {
    const path = location.pathname;

    if (path.startsWith('/platform/admin')) {
      if (path.includes('/users')) return { title: 'People', description: 'Accounts, roles and site access', icon: <PeopleIcon fontSize="small" /> };
      if (path.includes('/sites')) return { title: 'Sites', description: 'Tanks, capacities and where each pack is sent', icon: <LocalGasStationIcon fontSize="small" /> };
      if (path.includes('/deliveries')) return { title: 'Report delivery', description: 'Whether the daily packs are reaching anybody', icon: <MarkEmailReadIcon fontSize="small" /> };
      if (path.includes('/settings')) return { title: 'Operational settings', description: 'Float targets, premiums and what raises a flag', icon: <TuneIcon fontSize="small" /> };
      return { title: 'Control room', description: 'People, sites and the daily reports', icon: <AdminIcon fontSize="small" /> };
    }
    if (path.includes('expected-tankers')) return { title: 'Expected Tankers', description: 'Fuel stock, deliveries and ordering forecast', icon: <TimelineIcon fontSize="small" /> };
    if (path.includes('daily-sales')) return { title: 'Daily Sales', description: 'Upload, validate and review daily performance', icon: <SalesIcon fontSize="small" /> };
    if (path.includes('drive-offs-nmop')) return { title: 'Drive Offs & NMOP', description: 'Track incidents, reporting and recovery', icon: <ExceptionsIcon fontSize="small" /> };
    if (path.includes('reconciliation-weekly')) return { title: 'Weekly Reconciliation', description: 'Weekly banking and variance overview', icon: <ReconciliationIcon fontSize="small" /> };
    if (path.includes('reconciliation-daily') || path.endsWith('/reconciliation')) return { title: 'Reconciliation', description: 'Daily banking, cash and variance controls', icon: <ReconciliationIcon fontSize="small" /> };
    if (path.includes('golden-lines')) return { title: 'Golden Lines', description: 'Weekly product intelligence and performance', icon: <StarIcon fontSize="small" /> };
    if (path.includes('payments-approval')) return { title: 'Authorise Payments', description: 'Review and authorise a site’s monthly BACS run', icon: <GavelIcon fontSize="small" /> };
    if (path.includes('finance/overview') || path === '/platform/directors/payments') return { title: 'Payments Overview', description: 'Supplier payments across every site', icon: <PaymentsIcon fontSize="small" /> };
    if (path.includes('payments-due')) return { title: 'What’s Due', description: 'Unpaid invoices ordered by due date', icon: <EventNoteIcon fontSize="small" /> };
    if (path.includes('/statements')) return { title: 'Supplier Statements', description: 'Reconcile statements and find missing invoices', icon: <ReceiptLongIcon fontSize="small" /> };
    if (path.includes('/spend')) return { title: 'Spend Analysis', description: 'Supplier spend by month, category and site', icon: <InsightsIcon fontSize="small" /> };
    if (path.includes('quarterly-pack')) return { title: 'Quarterly Pack', description: 'Payment register for the accountant', icon: <SummarizeIcon fontSize="small" /> };
    if (path.includes('/suppliers')) return { title: 'Suppliers', description: 'Beneficiaries and encrypted bank details', icon: <StorefrontIcon fontSize="small" /> };
    if (path.includes('/payments')) return { title: 'BACS Payments', description: 'Monthly supplier payment run', icon: <PaymentsIcon fontSize="small" /> };
    if (path.includes('/users')) return { title: 'User Management', description: 'Accounts, roles and site access', icon: <PeopleIcon fontSize="small" /> };
    if (path.includes('dashboard') || path.includes('weekly-overview')) return { title: isDirectorsPlatform ? 'Weekly Overview' : 'Dashboard', description: isDirectorsPlatform ? 'Company performance across all sites' : 'Today’s site overview and priorities', icon: <DashboardIcon fontSize="small" /> };
    return { title: 'Valli Forecourts', description: 'Banking and reconciliation workspace', icon: <LayersIcon fontSize="small" /> };
  }, [location.pathname, isDirectorsPlatform]);

  const navItemSx = {
    position: 'relative',
    borderRadius: 2.7,
    mx: 1.15,
    my: 0.45,
    px: 1.05,
    py: 0.55,
    minHeight: 52,
    border: '1px solid transparent',
    backgroundColor: 'transparent',
    transition: 'transform .16s ease, background-color .16s ease, border-color .16s ease, box-shadow .16s ease',
    '& .MuiListItemIcon-root': {
      minWidth: 38,
      width: 36,
      height: 36,
      mr: 1.1,
      borderRadius: 2.1,
      display: 'grid',
      placeItems: 'center',
      backgroundColor: '#eef2f7',
      color: 'text.secondary',
      transition: 'all .16s ease',
    },
    '& .MuiListItemText-primary': {
      fontWeight: 850,
      fontSize: 14,
      lineHeight: 1.15,
    },
    '& .MuiListItemText-secondary': {
      mt: 0.28,
      fontSize: 10.8,
      lineHeight: 1.2,
      color: '#94a3b8',
    },
    '&:hover': {
      bgcolor: 'rgba(7,136,63,.065)',
      borderColor: '#cfe4d4',
      transform: 'translateX(2px)',
    },
    '&.Mui-selected': {
      borderColor: '#a9d5b4',
      boxShadow: '0 9px 22px rgba(7,136,63,.11)',
      background: 'linear-gradient(135deg, rgba(7,136,63,.13), rgba(101,184,46,.09))',
      '&::after': { content: '""', position: 'absolute', right: 7, top: 14, bottom: 14, width: 3.5, borderRadius: 999, background: 'linear-gradient(180deg,#07883f,#65b82e)' },
      '& .MuiListItemIcon-root': { color: '#fff', background: 'linear-gradient(135deg,#034f2d,#07883f)', boxShadow: '0 7px 16px rgba(7,136,63,.22)' },
      '& .MuiListItemText-primary': { color: '#034f2d', fontWeight: 950 },
      '& .MuiListItemText-secondary': { color: '#64748b' },
      '&:hover': { background: 'linear-gradient(135deg, rgba(7,136,63,.16), rgba(101,184,46,.11))' },
    },
  };

  /**
   * A collapsible nav group.
   *
   * BACS was three top-level entries — Payments, What's Due and Supplier
   * Statements — which pushed the site links off the first screen. They now sit
   * under one parent that opens on its own whenever you are inside it, so the
   * current page is never hidden behind a closed group.
   *
   * @param {Array} items  [{ label, secondary, to, icon, disabled }]
   */
  const navGroup = ({ id, title, secondary, icon, items }) => {
    const usable = items.filter(Boolean);
    if (!usable.length) return null;

    const childActive = usable.some((item) => item.to && isActive(item.to));
    const open = openNavGroups[id] ?? childActive;

    return (
      <React.Fragment key={id}>
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              ...navItemSx,
              ...(childActive && !open
                ? {
                    borderColor: '#a9d5b4',
                    background: 'linear-gradient(135deg, rgba(7,136,63,.09), rgba(101,184,46,.06))',
                  }
                : null),
            }}
            onClick={() =>
              setOpenNavGroups((prev) => ({ ...prev, [id]: !(prev[id] ?? childActive) }))
            }
          >
            <ListItemIcon
              sx={
                childActive
                  ? {
                      color: '#fff !important',
                      background: 'linear-gradient(135deg,#034f2d,#07883f) !important',
                      boxShadow: '0 7px 16px rgba(7,136,63,.22)',
                    }
                  : undefined
              }
            >
              {icon}
            </ListItemIcon>

            <ListItemText
              primary={title}
              secondary={secondary}
              sx={childActive ? { '& .MuiListItemText-primary': { color: '#034f2d' } } : undefined}
            />

            <ExpandMoreIcon
              fontSize="small"
              sx={{
                color: '#94a3b8',
                transition: 'transform .18s ease',
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </ListItemButton>
        </ListItem>

        <Collapse in={open} timeout="auto" unmountOnExit>
          {/* A rail down the left ties the children to their parent. */}
          <Box
            sx={{
              ml: 3.15,
              pl: 1.3,
              borderLeft: '2px solid',
              borderColor: childActive ? 'rgba(7,136,63,.25)' : '#e6ebf1',
            }}
          >
            {usable.map((item) => (
              <ListItem disablePadding key={item.label}>
                <ListItemButton
                  component={RouterLink}
                  to={item.to}
                  disabled={item.disabled}
                  selected={isActive(item.to)}
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    ...navItemSx,
                    mx: 0.6,
                    my: 0.3,
                    minHeight: 44,
                    borderRadius: 2.2,
                    '& .MuiListItemIcon-root': {
                      ...navItemSx['& .MuiListItemIcon-root'],
                      minWidth: 30,
                      width: 28,
                      height: 28,
                      mr: 0.9,
                      borderRadius: 1.6,
                    },
                    '& .MuiListItemText-primary': { fontWeight: 800, fontSize: 13 },
                    '& .MuiListItemText-secondary': { fontSize: 10.2, color: '#9aa7b4' },
                    '&.Mui-selected::after': { right: 5, top: 11, bottom: 11, width: 3 },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} secondary={item.secondary} />
                </ListItemButton>
              </ListItem>
            ))}
          </Box>
        </Collapse>
      </React.Fragment>
    );
  };

  const sectionLabel = (text) => (
    <Typography
      variant="caption"
      sx={{
        px: 2,
        pt: 1.7,
        pb: 0.65,
        display: 'block',
        fontWeight: 900,
        letterSpacing: 1.25,
        color: '#64748b',
        textTransform: 'uppercase',
      }}
    >
      {text}
    </Typography>
  );


  const isPurchasingManager =
  role === 'gl_manager' ||
  role === 'purchasing_manager' ||
  location.pathname.startsWith('/gl-manager');



  const drawer = (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background:
          'linear-gradient(180deg, rgba(7,136,63,.08) 0%, rgba(101,184,46,.035) 45%, rgba(0,0,0,0) 100%)',
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2.2,
          pb: 1.7,
          borderBottom: '1px solid',
          borderColor: 'rgba(255,255,255,.16)',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          background: 'linear-gradient(135deg,#021f15 0%,#034f2d 58%,#07883f 100%)',
          '&::after': { content: '""', position: 'absolute', width: 150, height: 150, right: -65, top: -85, borderRadius: '50%', background: 'rgba(255,255,255,.10)' },
          '& > *': { position: 'relative', zIndex: 1 },
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              background: 'rgba(255,255,255,.15)',
              border: '1px solid rgba(255,255,255,.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'common.white',
              boxShadow: '0 9px 20px rgba(15,23,42,.20)',
              flexShrink: 0,
            }}
          >
            <LayersIcon fontSize="small" />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ lineHeight: 1.1, fontWeight: 950, letterSpacing: '-.2px' }}>
              Valli Forecourts
            </Typography>
            <Typography variant="caption" sx={{ lineHeight: 1.1, fontWeight: 650, color: 'rgba(255,255,255,.76)' }}>
              Smart Banking &amp; Reconciliation
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5, maxWidth: '100%' }}>
          <Chip
            label={siteLabel}
            variant="filled"
            size="small"
            sx={{
              fontWeight: 900,
              borderRadius: 999,
              maxWidth: '100%',
              color: '#fff',
              bgcolor: 'rgba(255,255,255,.15)',
              border: '1px solid rgba(255,255,255,.27)',
              '& .MuiChip-label': {
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
          <Chip
            label={roleChipLabel}
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              maxWidth: '100%',
              color: '#034f2d',
              bgcolor: '#fff',
              borderColor: 'rgba(255,255,255,.82)',
            }}
          />
        </Stack>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pb: 1 }}>
        <List sx={{ pt: 0.5 }}>
          {isAdminPlatform ? (
            <>
              {sectionLabel('ADMIN')}

              {[
                { to: '/platform/admin', label: 'Dashboard', icon: <AdminIcon />, exact: true },
                { to: '/platform/admin/users', label: 'People', icon: <PeopleIcon /> },
                { to: '/platform/admin/sites', label: 'Sites', icon: <LocalGasStationIcon /> },
                { to: '/platform/admin/deliveries', label: 'Report delivery', icon: <MarkEmailReadIcon /> },
                { to: '/platform/admin/settings', label: 'Operational settings', icon: <TuneIcon /> },
              ].map((item) => (
                <ListItem disablePadding key={item.to}>
                  <ListItemButton
                    component={RouterLink}
                    to={item.to}
                    sx={navItemSx}
                    /*
                     * isActive is a startsWith, and every admin path starts
                     * with /platform/admin — so the dashboard entry has to
                     * match exactly or it lights up on all five screens.
                     */
                    selected={
                      item.exact
                        ? ['/platform/admin', '/platform/admin/', '/platform/admin/dashboard'].includes(
                            location.pathname
                          )
                        : isActive(item.to)
                    }
                    onClick={() => setMobileOpen(false)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </>
          ) : isDirectorsPlatform ? (
            <>
              {sectionLabel('DIRECTORS')}

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/weekly-overview"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/weekly-overview')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <TimelineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Weekly Overview" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/daily-sales"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/daily-sales')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <SalesIcon />
                  </ListItemIcon>
                  <ListItemText primary="Daily Sales" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/expected-tankers"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/expected-tankers')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <TimelineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Expected Tankers" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/drive-offs-nmop"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/drive-offs-nmop')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <ExceptionsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Drive Offs & NMOP" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/reconciliation-daily"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/reconciliation-daily')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <TimelineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Daily Reconciliation" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/reconciliation-weekly"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/reconciliation-weekly')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <TimelineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Weekly Reconciliation" />
                </ListItemButton>
              </ListItem>

              {sectionLabel('SUPPLIER PAYMENTS')}

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/payments"
                  sx={navItemSx}
                  selected={location.pathname === '/platform/directors/payments'}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <PaymentsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Payments Overview" secondary="All sites, totals and invoices" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/payments-approval"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/payments-approval')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <GavelIcon />
                  </ListItemIcon>
                  <ListItemText primary="Authorise Payments" secondary="Sign off a site's monthly run" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/spend"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/spend')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <InsightsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Spend Analysis" secondary="Where the money goes" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/suppliers"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/suppliers')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <StorefrontIcon />
                  </ListItemIcon>
                  <ListItemText primary="Suppliers" secondary="Bank details and verification" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/directors/quarterly-pack"
                  sx={navItemSx}
                  selected={isActive('/platform/directors/quarterly-pack')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <SummarizeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Quarterly Pack" secondary="Register for the accountant" />
                </ListItemButton>
              </ListItem>
            </>
          ) : isFinancePlatform ? (
            <>
              {sectionLabel('FINANCE')}

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/finance/overview"
                  sx={navItemSx}
                  selected={isActive('/platform/finance/overview')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary="Payments Overview" secondary="All sites, totals and invoices" />
                </ListItemButton>
              </ListItem>

              {/* Building a run is finance's job. An accountant reads the
                  register and the quarterly pack; they never raise a payment,
                  so that child is simply absent for them. */}
              {navGroup({
                id: 'finance-bacs',
                title: 'BACS Payments',
                secondary: 'Supplier payments and invoices',
                icon: <PaymentsIcon />,
                items: [
                  isAccountant
                    ? null
                    : {
                        label: 'Payment Run',
                        secondary: 'Build and submit monthly runs',
                        to: '/platform/finance/payments',
                        icon: <PaymentsIcon sx={{ fontSize: 17 }} />,
                      },
                  {
                    label: "What's Due",
                    secondary: 'Unpaid invoices by due date',
                    to: '/platform/finance/payments-due',
                    icon: <EventNoteIcon sx={{ fontSize: 17 }} />,
                  },
                  {
                    label: 'Supplier Statements',
                    secondary: 'Find missing invoices',
                    to: '/platform/finance/statements',
                    icon: <ReceiptLongIcon sx={{ fontSize: 17 }} />,
                  },
                ],
              })}

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/finance/spend"
                  sx={navItemSx}
                  selected={isActive('/platform/finance/spend')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <InsightsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Spend Analysis" secondary="Where the money goes" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/finance/suppliers"
                  sx={navItemSx}
                  selected={isActive('/platform/finance/suppliers')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <StorefrontIcon />
                  </ListItemIcon>
                  <ListItemText primary="Suppliers" secondary="Bank details and verification" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/platform/finance/quarterly-pack"
                  sx={navItemSx}
                  selected={isActive('/platform/finance/quarterly-pack')}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <SummarizeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Quarterly Pack" secondary="Register for the accountant" />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              {sectionLabel('SITE')}

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={`/platform/site/${effectiveSiteCode}/dashboard`}
                  sx={navItemSx}
                  selected={isActive(`/platform/site/${effectiveSiteCode}/dashboard`)}
                  disabled={!effectiveSiteCode}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <DashboardIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" secondary="Daily site overview" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={`/platform/site/${effectiveSiteCode}/reconciliation`}
                  sx={navItemSx}
                  selected={isActive(`/platform/site/${effectiveSiteCode}/reconciliation`)}
                  disabled={!effectiveSiteCode}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <ReconciliationIcon />
                  </ListItemIcon>
                  <ListItemText primary="Reconciliation" secondary="Banking and cash controls" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={`/platform/site/${effectiveSiteCode}/daily-sales`}
                  sx={navItemSx}
                  selected={isActive(`/platform/site/${effectiveSiteCode}/daily-sales`)}
                  disabled={!effectiveSiteCode}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <SalesIcon />
                  </ListItemIcon>
                  <ListItemText primary="Daily Sales" secondary="Sales entry and performance" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={`/platform/site/${effectiveSiteCode}/expected-tankers`}
                  sx={navItemSx}
                  selected={isActive(`/platform/site/${effectiveSiteCode}/expected-tankers`)}
                  disabled={!effectiveSiteCode}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <TimelineIcon />
                  </ListItemIcon>
                  <ListItemText primary="Expected Tankers" secondary="Fuel delivery schedule" />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={`/platform/site/${effectiveSiteCode}/drive-offs-nmop`}
                  sx={navItemSx}
                  selected={isActive(`/platform/site/${effectiveSiteCode}/drive-offs-nmop`)}
                  disabled={!effectiveSiteCode}
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <ExceptionsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Drive Offs & NMOP" secondary="Incident and exception log" />
                </ListItemButton>
              </ListItem>

              {navGroup({
                id: 'site-bacs',
                title: 'BACS Payments',
                secondary: 'Supplier payments and invoices',
                icon: <PaymentsIcon />,
                items: [
                  {
                    label: 'Payment Run',
                    secondary: 'Monthly supplier payment run',
                    to: `/platform/site/${effectiveSiteCode}/payments`,
                    icon: <PaymentsIcon sx={{ fontSize: 17 }} />,
                    disabled: !effectiveSiteCode,
                  },
                  {
                    label: "What's Due",
                    secondary: 'Unpaid invoices by due date',
                    to: `/platform/site/${effectiveSiteCode}/payments-due`,
                    icon: <EventNoteIcon sx={{ fontSize: 17 }} />,
                    disabled: !effectiveSiteCode,
                  },
                  {
                    label: 'Supplier Statements',
                    secondary: 'Find missing invoices',
                    to: `/platform/site/${effectiveSiteCode}/statements`,
                    icon: <ReceiptLongIcon sx={{ fontSize: 17 }} />,
                    disabled: !effectiveSiteCode,
                  },
                ],
              })}
            </>
          )}
        </List>

        <Divider sx={{ mt: 1.5 }} />

        <List sx={{ pt: 0.5 }}>
          {!isAdminPlatform && !isFinancePlatform ? (
            <>
              {sectionLabel('EXTRAS')}

              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={
                    isDirectorsPlatform
                      ? '/platform/directors/golden-lines'
                      : effectiveSiteCode
                        ? `/platform/site/${effectiveSiteCode}/golden-lines`
                        : '#'
                  }
                  disabled={!isDirectorsPlatform && !effectiveSiteCode}
                  sx={navItemSx}
                  selected={
                    isDirectorsPlatform
                      ? isActive('/platform/directors/golden-lines')
                      : isActive(`/platform/site/${effectiveSiteCode}/golden-lines`)
                  }
                  onClick={() => setMobileOpen(false)}
                >
                  <ListItemIcon>
                    <StarIcon />
                  </ListItemIcon>
                  <ListItemText primary="Golden Lines" secondary="Weekly product intelligence" />
                </ListItemButton>
              </ListItem>
            </>
          ) : null}
        </List>
      </Box>

      <Box sx={{ p: 1.5, flexShrink: 0 }}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3.2,
            p: 1.5,
            borderColor: '#cfe4d4',
            background: 'linear-gradient(145deg, #edf7ef 0%, #ffffff 52%, #f4faef 100%)',
            boxShadow: '0 12px 28px rgba(3,79,45,.10)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': { content: '""', position: 'absolute', inset: '0 0 auto', height: 3, background: 'linear-gradient(90deg,#034f2d,#65b82e)' },
          }}
        >
          <Typography variant="overline" sx={{ display: 'block', mb: 0.7, color: '#64748b', fontSize: 9.5, lineHeight: 1, fontWeight: 950, letterSpacing: 1.2 }}>SIGNED IN AS</Typography>
          <Stack direction="row" spacing={1.1} alignItems="center">
            <Avatar sx={{ width: 38, height: 38, fontSize: 13, fontWeight: 950, background: 'linear-gradient(135deg,#034f2d,#07883f)' }}>
              {String(displayName || 'U').split(/\s+/).map((part) => part.charAt(0)).join('').slice(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, lineHeight: 1.2 }} noWrap>{displayName}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">{emailLabel || '—'}</Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.25, maxWidth: '100%' }}>
            <Chip
              size="small"
              label={siteLabel}
              variant="outlined"
              sx={{
                borderRadius: 999,
                fontWeight: 800,
                maxWidth: '100%',
                bgcolor: 'rgba(255,255,255,.75)',
                borderColor: '#a9d5b4',
                color: '#034f2d',
                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            />
            <Chip
              size="small"
              label={roleChipLabel}
              color="primary"
              sx={{
                borderRadius: 999,
                fontWeight: 900,
                maxWidth: '100%',
                background: 'linear-gradient(135deg,#034f2d,#07883f)',
                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            />
          </Stack>

          <Box sx={{ mt: 1.25 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: '#dbe4f0',
                  backgroundColor: 'rgba(255,255,255,.86)',
                  color: '#334155',
                  '&:hover': { bgcolor: '#fff1f2', borderColor: '#fecdd3', color: '#be123c' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 900, fontSize: 14 }} />
              </ListItemButton>
            </ListItem>
          </Box>
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        minWidth: 0,
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      <AppBar
  position="fixed"
  sx={{
    width: { md: `calc(100% - ${drawerWidth}px)` },
    ml: { md: `${drawerWidth}px` },
    background: 'linear-gradient(110deg,#021f15 0%,#034f2d 58%,#07883f 100%)',
    boxShadow: '0 5px 18px rgba(15,23,42,.18)',
  }}
>
  <Toolbar sx={{ gap: 1.25, minHeight: isPurchasingManager ? 78 : 68, px: { xs: 1.5, sm: 2.25 } }}>
    <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ display: { md: 'none' } }}>
      <MenuIcon />
    </IconButton>

    {isPurchasingManager ? (
      <>
        <Box
          className="vf-page-scroll"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2.5,
            bgcolor: 'rgba(255,255,255,0.16)',
            border: '1px solid rgba(255,255,255,0.28)',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <StarIcon />
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 950,
              lineHeight: 1.15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Valli Forecourts Purchasing Manager Dashboard
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.82)',
              fontWeight: 700,
              letterSpacing: 0.3,
            }}
          >
            Golden Lines • Site Performance • Product Analysis • Purchasing Intelligence
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label="LIVE"
            size="small"
            sx={{
              bgcolor: 'rgba(34,197,94,0.18)',
              color: '#dcfce7',
              border: '1px solid rgba(187,247,208,0.55)',
              fontWeight: 950,
              borderRadius: 999,
            }}
          />

          <Chip
            label={roleChipLabel || 'MANAGER'}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.14)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.35)',
              fontWeight: 950,
              borderRadius: 999,
            }}
          />

          <Tooltip title={displayName}>
            <Chip
              label={`Welcome, ${displayName}`}
              size="small"
              sx={{
                bgcolor: 'white',
                color: '#034f2d',
                fontWeight: 900,
                borderRadius: 999,
                maxWidth: 230,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          </Tooltip>
        </Stack>
      </>
    ) : (
      <>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.25,
            display: { xs: 'none', sm: 'grid' },
            placeItems: 'center',
            bgcolor: 'rgba(255,255,255,.14)',
            border: '1px solid rgba(255,255,255,.24)',
            flexShrink: 0,
          }}
        >
          {pageMeta.icon}
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={pageMeta.title}
          >
            {pageMeta.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: { xs: 'none', sm: 'block' }, color: 'rgba(255,255,255,.76)', fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {pageMeta.description}
          </Typography>
        </Box>

        <Chip
          label={siteLabel}
          size="small"
          sx={{ display: { xs: 'none', lg: 'flex' }, maxWidth: 230, bgcolor: 'rgba(255,255,255,.12)', color: '#fff', border: '1px solid rgba(255,255,255,.28)', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
        />

        <Tooltip title={`${displayName} · ${roleChipLabel}`}>
          <Chip
            className="vf-appbar-user-chip"
            avatar={<Avatar sx={{ bgcolor: '#fff !important', color: '#034f2d !important', fontWeight: 900 }}>{String(displayName).charAt(0).toUpperCase()}</Avatar>}
            label={displayName}
            size="small"
            sx={{
              bgcolor: '#fff',
              borderRadius: 999,
              color: '#034f2d',
              border: '1px solid rgba(255,255,255,.55)',
              fontWeight: 900,
              maxWidth: { xs: 120, sm: 190 },
              '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
            }}
          />
        </Tooltip>
      </>
    )}
  </Toolbar>
</AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              height: '100vh',
              overflow: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              height: '100vh',
              overflow: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minWidth: 0,
          height: '100vh',
          maxHeight: '100vh',
          backgroundColor: 'background.default',
          overflow: 'hidden',
        }}
      >
        <Toolbar sx={{ flexShrink: 0, minHeight: isPurchasingManager ? '78px !important' : '68px !important' }} />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            p: { xs: 0.75, sm: 1.25, md: 1.5, xl: 1.75 },
            boxSizing: 'border-box',
            WebkitOverflowScrolling: 'touch',
            background: 'radial-gradient(circle at 12% 0%, rgba(7,136,63,.07), transparent 28%), #f3f8f3',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
